import type {ReferralFiltersPayload} from 'src/types/referral-v2';
import type {GetReferralTrackingApiResponse} from 'src/types/referrer-portal';
import * as React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {useInflight} from 'src/hooks/redux';
import useAnalyticsQuery from 'src/components/analytics/useAnalyticsQuery';
import debounce from 'lodash/debounce';
import {DEFAULT_TABLE_PAGE_SIZE} from 'src/utils/referral-v2';
import {resolveDateRange} from 'src/utils/date-range';
import {getAllReferralsDetails} from 'src/action-creators/referrer-portal/tracking';
import {selectReferralRequest} from 'src/selectors/referrer-portal';
import {getHeaders} from 'src/components/referrer-portal/my-referrals/referrals/referrals-table-header';
import {
  getFormattedDateForApi,
  getTableBorderRadius,
} from 'src/components/referral-v2/common';

import {
  SearchInput,
  Pagination,
  Table,
  TableTopBar,
  TableBottomBar,
  SubTitleExtraSmall,
  EmptyState,
} from '@spaced-out/ui-design-system/lib/components/index';
import {
  Filter,
  AgencyAndProgramFilter,
} from 'src/components/referrer-portal/my-referrals/filters';
import css from 'src/components/referral-v2/tracking/tracking.css';


const Tracking = () => {
  const {t, language} = useLabelTranslation();
  const dispatch = useDispatch();
  const [query] = useAnalyticsQuery();
  const {start_date = '', end_date = ''} = resolveDateRange(query);
  const start_date_formatted_date = getFormattedDateForApi(start_date);
  const end_date_formatted_date = getFormattedDateForApi(end_date);

  const defaultFilters = [
    {
      key: 'timeframe',
      values: {
        start_date: start_date_formatted_date,
        end_date: end_date_formatted_date,
      },
    },
  ];

  const [searchText, setSearchText] = React.useState('');
  const [agencyId, setAgencyId] = React.useState<any>(null);
  const [programId, setProgramId] = React.useState<any>(null);
  const [pageNumber, setPageNumber] = React.useState(1);
  const [filters, setFilters] =
    React.useState<ReferralFiltersPayload[]>(defaultFilters);

  const callApi = (value: string) => {
    const filtersData = [...filters];
    if (value) {
      filtersData.push({key: 'name', value});
    }
    if (agencyId) {
      filtersData.push({key: 'agency_id', value: agencyId});
    }
    if (programId) {
      filtersData.push({key: 'program_id', value: programId});
    }
    const filtersPayload = {
      filters: filtersData,
      limit: DEFAULT_TABLE_PAGE_SIZE,
      offset: (pageNumber - 1) * DEFAULT_TABLE_PAGE_SIZE,
    } as const;
    dispatch(getAllReferralsDetails(filtersPayload));
  };

  const debounceFn = React.useCallback(
    debounce(callApi, 500, {
      trailing: true,
    }),
    [],
  );

  const handlePageChange = (value: number | null | undefined) => {
    if (!value) {
      return;
    }
    setPageNumber(value);
  };

  const handleClear = () => {
    setPageNumber(1);
    setSearchText('');
  };

  React.useEffect(() => {
    callApi(searchText);
  }, [pageNumber, filters, programId, agencyId]);

  // @ts-expect-error - TS2322 - Type 'import("/Users/diwaker/SpacedOut/ui-server/src/types/referral-v2").GetReferralTrackingApiResponse' is not assignable to type 'import("/Users/diwaker/SpacedOut/ui-server/src/types/referrer-portal").GetReferralTrackingApiResponse'.
  const allReferralsDetails: GetReferralTrackingApiResponse | null | undefined =
    useSelector(selectReferralRequest);

  const isReferralsDataLoading = useInflight(
    getAllReferralsDetails({
      filters: defaultFilters,
      limit: DEFAULT_TABLE_PAGE_SIZE,
      offset: (pageNumber - 1) * DEFAULT_TABLE_PAGE_SIZE,
    }),
  );

  const onAgencyAndProgramFilterChange = (
    filterName: string,
    value: string | null | undefined,
  ) => {
    if (filterName === 'agency_id') {
      setAgencyId(value);
    } else {
      setProgramId(value);
    }
    handleClear();
  };

  const handleApplyFilter = (filters: Array<ReferralFiltersPayload>) => {
    setFilters([...filters]);
    handleClear();
  };

  return (
    <div className={css.referralPortaltrackingContainer}>
      <TableTopBar>
        <div className={css.defaultGap}>
          <SearchInput
            onClear={() => {
              setSearchText('');
              callApi('');
            }}
            value={searchText}
            placeholder={t(
              'Search_by_referral_name',
              'Search by referral name',
            )}
            classNames={{wrapper: css.trackingSearch}}
            onChange={(e) => {
              setSearchText(e.target.value);
              debounceFn(e.target.value);
            }}
          />
          <Filter
            from="tracking"
            searchText={searchText}
            handleApplyFilter={handleApplyFilter}
          />
        </div>
        <AgencyAndProgramFilter
          agencyId={agencyId}
          programId={programId}
          onChangeFilter={onAgencyAndProgramFilterChange}
        />
      </TableTopBar>
      <Table
        borderRadius={getTableBorderRadius(allReferralsDetails?.total_pages)}
        entries={allReferralsDetails ? allReferralsDetails.tracking : []}
        stickyHeader={true}
        headers={getHeaders(
          allReferralsDetails ? allReferralsDetails.file_container : '',
          t,
          language,
        )}
        isLoading={isReferralsDataLoading}
        classNames={{
          wrapper: css.tableWrapper,
          tableBody: css.tableBody,
        }}
        emptyText={
          <EmptyState
            description={
              searchText
                ? `${t(
                    'Doublecheck_your_spelling_or_try_using_different_keywords',
                    'Double-check your spelling or try using different keywords',
                  )}.`
                : `${t(
                    'Once_the_candidates_are_referred_the_list_will_be_displayed_here',
                    'Once the candidates are referred, the list will be displayed here',
                  )}.`
            }
            imageVariant="data"
            title={
              searchText
                ? t('No_results_found', 'No results found')
                : t(
                    'No_candidates_have_been_referred',
                    'No candidates have been referred',
                  )
            }
            classNames={{wrapper: css.emptyTableContainer}}
          />
        }
        idName="referral_id"
      />

      {allReferralsDetails && allReferralsDetails.total_pages > 1 && (
        <TableBottomBar>
          <Pagination
            currentPage={pageNumber}
            onChange={handlePageChange}
            totalPages={allReferralsDetails.total_pages}
          >
            <SubTitleExtraSmall color="secondary">
              {`${t('Showing_page', 'Showing page')} `}
              {pageNumber}
              {` ${t('of', 'of')} `}
              {allReferralsDetails.total_pages}
            </SubTitleExtraSmall>
          </Pagination>
        </TableBottomBar>
      )}
    </div>
  );
};

export default Tracking;
